/**
 * TanStack Query hooks over `connectors.*`. Single `['connectors']`
 * namespace; any mutation invalidates the whole thing, which is
 * cheap and keeps the Vault's per-GI chip list in sync without a
 * second cross-feature key.
 *
 * Every mutation fires a localised success toast on resolve so the
 * user gets consistent feedback regardless of which call-site
 * triggered it (the config dialog, the row actions, or a future
 * inline action).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { connectors } from '@/api/endpoints/connectors';
import type {
  ConnectorCreateInput,
  ConnectorUpdateInput,
  EntityId,
} from '@/api/types';
import { useToast } from '@/components/Toaster';

export const connectorKeys = {
  list: ['connectors', 'list'] as const,
  detail: (id: string) => ['connectors', 'detail', id] as const,
  forGi: (root_id: EntityId) => ['connectors', 'for_gi', root_id] as const,
};

export function useConnectorList() {
  return useQuery({
    queryKey: connectorKeys.list,
    queryFn: () => connectors.list(),
    staleTime: 30_000,
  });
}

export function useConnectorsForGi(root_id: EntityId | undefined) {
  return useQuery({
    queryKey: connectorKeys.forGi(root_id ?? ''),
    queryFn: () => (root_id ? connectors.listForGi(root_id) : Promise.resolve([])),
    enabled: Boolean(root_id),
    staleTime: 30_000,
  });
}

export function useConnectorDetail(id: string | undefined) {
  return useQuery({
    queryKey: connectorKeys.detail(id ?? ''),
    queryFn: () => (id ? connectors.get(id) : Promise.resolve(null)),
    enabled: Boolean(id),
  });
}

export function useCreateConnector() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: ConnectorCreateInput) => connectors.create(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connectors'] });
      toast.success(t('toast.created'));
    },
  });
}

export function useUpdateConnector() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (input: ConnectorUpdateInput) => connectors.update(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connectors'] });
      toast.success(t('toast.saved'));
    },
  });
}

export function useDeleteConnector() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => connectors.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connectors'] });
      toast.success(t('toast.deleted'));
    },
  });
}

export function useTestConnector() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => connectors.test(id),
    onSuccess: () => {
      // last_tested_at/last_message mutate; refresh list + detail.
      void qc.invalidateQueries({ queryKey: ['connectors'] });
      toast.success(t('toast.connection_tested'));
    },
  });
}
